import { DfmFeatures } from 'fictiv-3d-viewer'

import { TripleFloatType, ViewerStateType, P2PPoint } from './types'

export const MM = 'mm'
export const IN = 'in'

export const NEW_COMMENT_ID = '__new__' // should be in sync with NEW_ANNOTATION_ID in old viewer

/* Viewer interaction modes */
export const ANNOTATION_MODE = 'ANNOTATION_MODE'
export const TAPS_INSPECTION_MODE = 'TAPS_INSPECTION_MODE'
export const CROSS_SECTION_ANNOTATION_MODE = 'CROSS_SECTION_ANNOTATION_MODE'
export const CROSS_SECTION_MODE = 'CROSS_SECTION_MODE'
export const SELECT_MODE = 'SELECT_MODE'
export const DFM_INSPECTION_MODE = 'DFM_INSPECTION_MODE'
export const DFM_FEEDBACK_MODE = 'DFM_FEEDBACK_MODE'
export const POINT_TO_POINT_MODE = 'POINT_TO_POINT_MODE'

export const FRONT = 'front'
export const BACK = 'back'
export const LEFT = 'left'
export const RIGHT = 'right'
export const TOP = 'top'
export const BOTTOM = 'bottom'

export const SURFACE_COMMENT = 'surface'
export const PIN_COMMENT = 'pin'
export const XSECTION_COMMENT = 'xsection'

export const DEFAULT_COLOR = '#16bc9c'
export const ACTIVE_COLOR = '#91e8d7'
export const ALERT_COLOR = '#e74330'

export const CUSTOM = 'custom'
export const POS_X = '+X'
export const POS_Y = '+Y'
export const POS_Z = '+Z'
export const NEG_X = '-X'
export const NEG_Y = '-Y'
export const NEG_Z = '-Z'

const PALETTE_COLORS = [
    '#16BC9C',
    '#149CB8',
    '#1469B8',
    '#4D4CDF',
    '#6F51C8',
    '#8F3D8F',
    '#993355',
    '#E74330',
    '#D96626',
    '#D9B426',
    '#B3D926',
    '#4EC069',
]

export const DEFAULT_CROSS_SECTION_CONFIG = {
    mode: SELECT_MODE,
    enabled: false,
    view: null,
    toolsHidden: true,
    hoveredTool: null,
    position: [0, 0, 0],
    rotation: [0, 0, 0, 0],
}

export type DfmFeaturesTypes = typeof DfmFeatures[keyof typeof DfmFeatures]

export const TEXTURED_FEATURES: DfmFeaturesTypes[] = [
    DfmFeatures.THINWALL,
    DfmFeatures.UNREACHABLE_SURFACE,
    DfmFeatures.SINK_ANALYSIS,
    DfmFeatures.THICKNESS_ANALYSIS,
    DfmFeatures.DRAFT_ANALYSIS,
    DfmFeatures.CORE_CAVITY_ANALYSIS,
    DfmFeatures.SIDE_ACTIONS,
]

export const TEXTURED_FEATURES_WITH_THRESHOLD: DfmFeaturesTypes[] = [
    DfmFeatures.THINWALL,
    DfmFeatures.SINK_ANALYSIS,
    DfmFeatures.THICKNESS_ANALYSIS,
    DfmFeatures.DRAFT_ANALYSIS,
]

export const TEXTURED_FEATURES_WITH_DIRECTION: DfmFeaturesTypes[] = [
    DfmFeatures.DRAFT_ANALYSIS,
]

export const DEFAULT_STATE: ViewerStateType = Object.freeze({
    /* Hole/Hole group interactions */
    hoveredHoleGroupId: null,
    hoveredHoleId: null,
    selectedTapsPatchHash: null,
    hoveredTapsPatchHash: null,

    /* Controls */
    transparent: false,
    displayRawInspection: false,
    displayPdfInspection: false,
    activeDfmFeatures: [] as DfmFeaturesTypes[],
    textureThreshold: null,
    units: MM, // mm | in
    annotatable: false,
    forceHideCrossSectionTools: false, // an override for manual control
    /* interaction modes */
    interactionMode: SELECT_MODE,
    selectedPullDirection: null,
    selectedPullDirectionPatchHash: null,

    /* Mode specific interactions */
    paletteColors: PALETTE_COLORS,
    selectedPaletteColor: PALETTE_COLORS[0],
    selectedPatchConfigs: [] as { hashcode: string; color: string }[],
    annotations: {}, // annotation configs
    cameraConfig: null,
    defaultCameraConfig: null,
    partRotation: null,
    crossSectionConfig: DEFAULT_CROSS_SECTION_CONFIG,
    selectedPoints: [] as P2PPoint[], // measurement points
    hoveredPoint: null,
    customP2PPoints: [] as number[][], // [[p0x, p0y, p0z], [p1x, ...], ... ]array

    /* General interactions */
    hoveredPatch: null,
    hoveredPatches: [] as string[],
    hoveredHighlightPatchHash: null,

    /* Commenting */
    hoveredCommentId: null,
    selectedCommentId: null,

    /* Dfm card interactions */
    selectedDfmId: null,
    hoveredDfmId: null,
    selectAllDfmByDefault: false,

    /* Tools on top of the viewer
    (ColorPicker, Cross section mode buttons, transparent/reset camera buttons, MeasurePanel) */
    toolVisibility: {
        infoBar: true,
        interactionControlBox: true,
        measurePanel: true,
    },

    /* Fictiv 3d viewer instance handle */
    viewer: null,
} as const)

export const PULL_DIRECTION_MAP: Record<string, TripleFloatType | null> = {
    '+X': [1, 0, 0],
    '-X': [-1, 0, 0],
    '+Y': [0, 1, 0],
    '-Y': [0, -1, 0],
    '+Z': [0, 0, 1],
    '-Z': [0, 0, -1],
    custom: null,
}
